// P5 会话与 vault 状态机（完整端的"大脑"）：login → bootstrap/unlock → workspace，
// 以及编辑→封装→同步（走 P2 pushVault 引擎）。明文/VDK 只在内存；锁定/退出即清。

import { reactive, ref, shallowRef } from 'vue'
import * as api from './api'
import {
  openVault, decryptPayload, openField, sealField, passwordFingerprint, zeroize,
  rewrapVaultPin, readMeta, packVault, resealPayload, bytesToBase64,
  generateRecoveryCode, recoveryCodeFingerprint,
  type OpenedVault,
} from './crypto'
import { buildNewCloudVault } from './lib/bootstrap'
import { pushVault, type VaultPayload, type MergeConflict, type Entity } from './sync'
import { estimateScore } from './lib/generator'
import { classifyStrength } from './lib/risk'
import type { AccountItem, SecretItem } from './lib/model'

export type Stage = 'login' | 'setup' | 'unlock' | 'workspace'
export type SyncState = 'idle' | 'syncing' | 'synced' | 'conflict' | 'error'

function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v))
}

function emptyPayload(): VaultPayload {
  return { accounts: [], secrets: [], categories: [], projects: [], risks: [], ignored_risk_keys: [], recent: [], audit: [] }
}

export interface Session {
  stage: Stage
  busy: boolean
  busyStep: string
  error: string
  notice: string
  // 账户/会员
  account: api.AccountView | null
  membership: api.MembershipView | null
  hasVault: boolean
  // 解锁后
  payload: VaultPayload
  syncState: SyncState
  conflicts: MergeConflict[]
  lastRecoveryCode: string // bootstrap 时一次性展示
}

const deviceId = (() => {
  const a = new Uint8Array(9)
  crypto.getRandomValues(a)
  return 'web_' + btoa(String.fromCharCode(...a)).replace(/[^a-zA-Z0-9]/g, '').slice(0, 12)
})()

export const session = reactive<Session>({
  stage: 'login',
  busy: false,
  busyStep: '',
  error: '',
  notice: '',
  account: null,
  membership: null,
  hasVault: false,
  payload: emptyPayload(),
  syncState: 'idle',
  conflicts: [],
  lastRecoveryCode: '',
})

// 内存敏感态（不进响应式，避免被序列化/暴露）。
const opened = shallowRef<OpenedVault | null>(null)
let meta: Record<string, unknown> | null = null
let basePayload: VaultPayload = emptyPayload()
let version = 0
let sServerB64 = ''
export const phoneRef = ref('')

export function vdk(): Uint8Array | null {
  return opened.value?.vdk ?? null
}

function friendly(e: unknown): string {
  const err = e as { status?: number; message?: string; body?: { msg?: string; error?: string } }
  const status = err?.status
  if (status === 401) return '账号或验证码不正确'
  if (status === 403) return '需要守狸云会员才能使用云端保险箱'
  if (status === 429) return '今日操作次数已达上限'
  if (status === 502 || status === 503) return '云端暂时不可达，请稍后再试'
  if (/PIN 或 S_server/.test(err?.message || '')) return '主密码不正确，请再试一次'
  return err?.body?.msg || err?.message || String(e)
}

// ── 登录后初始化（身份层之后的统一入口）────────────────────────────────────
/**
 * 身份层（SSO 静默续登 / PKCE 回调 / loginPhone 回落）拿到 account access token 后调用：
 * 登记设备 → 读账户/会员 → 探测云端 vault → 落到 setup（新建）或 unlock（解锁）阶段。
 * token 已由 SDK 持有（经 api.setTokenProvider 桥接），本函数不碰身份凭据，只做产品侧编排。
 */
export async function initAfterAuth(): Promise<void> {
  session.error = ''
  session.busy = true
  try {
    session.busyStep = '正在登记设备…'
    await api.registerDevice('Keytail Web 完整端').catch(() => undefined)
    session.busyStep = '正在读取账户与会员状态…'
    session.account = await api.getAccount().catch(() => null)
    session.membership = await api.getMembership().catch(() => null)
    session.busyStep = '正在检查云端保险箱…'
    const vault = await api.fetchVault()
    session.hasVault = !!vault
    session.stage = vault ? 'unlock' : 'setup'
  } catch (e) {
    session.error = friendly(e)
  } finally {
    session.busy = false
    session.busyStep = ''
  }
}

// ── 短信直登回落（ADR D：SSO 主路外保留，便于测试环境 soundlan 联调）──────────
export async function sendCode(phone: string): Promise<string> {
  const r = await api.sendSmsCode(phone)
  return r.devCode || ''
}

export async function login(phone: string, smsCode: string): Promise<void> {
  session.error = ''
  session.busy = true
  try {
    session.busyStep = '正在验证账号…'
    await api.loginPhone(phone, smsCode)
    phoneRef.value = phone
  } catch (e) {
    session.error = friendly(e)
    session.busy = false
    session.busyStep = ''
    return
  }
  session.busy = false
  await initAfterAuth()
}

async function assistSServer(): Promise<string> {
  const a = await api.unlockAssist(deviceId)
  return a.sServerB64
}

// ── 新建（bootstrap）──────────────────────────────────────────────────────
export async function bootstrap(pin: string): Promise<void> {
  session.error = ''
  session.busy = true
  try {
    session.busyStep = '正在向云端申请密钥分片…'
    sServerB64 = await assistSServer()
    session.busyStep = '正在本地生成保险箱…'
    const { blobB64, recoveryCode } = buildNewCloudVault(pin, sServerB64)
    session.busyStep = '正在上传云端…'
    const view = await api.uploadVault(0, blobB64)
    version = view.version
    enterWorkspace(openVault(blobB64, pin, sServerB64))
    session.lastRecoveryCode = recoveryCode
    session.hasVault = true
  } catch (e) {
    session.error = friendly(e)
  } finally {
    session.busy = false
    session.busyStep = ''
  }
}

// ── 解锁现有库 ─────────────────────────────────────────────────────────────
export async function unlock(pin: string): Promise<void> {
  session.error = ''
  session.busy = true
  try {
    session.busyStep = '正在向云端申请解锁协助…'
    sServerB64 = await assistSServer()
    session.busyStep = '正在拉取云端快照…'
    const vault = await api.fetchVault()
    if (!vault) {
      session.error = '云端保险箱不存在，请改用新建。'
      session.stage = 'setup'
      return
    }
    version = vault.version
    session.busyStep = '正在本地解密…'
    enterWorkspace(openVault(vault.vaultBlobB64, pin, sServerB64))
  } catch (e) {
    session.error = friendly(e)
  } finally {
    session.busy = false
    session.busyStep = ''
  }
}

function enterWorkspace(o: OpenedVault): void {
  opened.value = o
  meta = o.meta
  session.payload = normalize(o.payload)
  basePayload = clone(session.payload)
  session.syncState = 'synced'
  session.conflicts = []
  session.stage = 'workspace'
}

function normalize(p: any): VaultPayload {
  return { ...emptyPayload(), ...p }
}

// ── 编辑 + 同步 ────────────────────────────────────────────────────────────
/** 修改内存 payload 后触发同步回写。mutator 直接改 session.payload。 */
export async function mutateAndSync(mutator: () => void): Promise<void> {
  mutator()
  await sync()
}

export async function sync(): Promise<void> {
  const o = opened.value
  if (!o || !meta) return
  session.syncState = 'syncing'
  session.error = ''
  try {
    const res = await pushVault(meta, o.vdk, basePayload, clone(session.payload), version, {
      fetchRemote: async () => {
        const v = await api.fetchVault()
        if (!v) throw new Error('远端 vault 消失')
        return { version: v.version, payload: normalize(decryptPayload(v.vaultBlobB64, o.vdk)) }
      },
    })
    version = res.version
    session.payload = normalize(res.payload)
    basePayload = clone(session.payload)
    session.conflicts = res.conflicts
    session.syncState = res.conflicts.length ? 'conflict' : 'synced'
  } catch (e) {
    session.syncState = 'error'
    session.error = friendly(e)
    throw e
  }
}

// ── 字段读取 ───────────────────────────────────────────────────────────────
/** 按需解一段字段密文为明文（reveal/copy 用）。 */
export function revealField(ciphertextB64: string | null | undefined): string {
  const o = opened.value
  if (!o || !ciphertextB64) return ''
  return openField(o.vdk, ciphertextB64)
}

function uuid(): string {
  try {
    return crypto.randomUUID()
  } catch {
    const a = new Uint8Array(12)
    crypto.getRandomValues(a)
    return 'id_' + btoa(String.fromCharCode(...a)).replace(/[^a-zA-Z0-9]/g, '').slice(0, 16)
  }
}

export interface AccountDraft {
  id?: string
  platform_name: string
  platform_url?: string
  username?: string
  email?: string
  phone?: string
  password?: string // 明文；空 = 保留原密文（编辑时）
  category_id?: string
  project_id?: string
  has_2fa: boolean
  favorite: boolean
  note?: string
  last_password_changed_at?: string | null
}

export interface SecretDraft {
  id?: string
  name: string
  platform_name?: string
  kind: string
  key_id?: string
  secret?: string // 明文；空 = 保留原密文（编辑时）
  environment?: string
  project_id?: string
  rotation_cycle_days?: number | null
  note?: string
}

function nz(s?: string): string | null {
  return s && s.trim() ? s.trim() : null
}

/** 新建/更新账号：封装密码/备注、算指纹与强度、写 updated_at，然后同步。 */
export async function upsertAccount(draft: AccountDraft): Promise<void> {
  const o = opened.value
  if (!o) return
  const now = new Date().toISOString()
  const existing = draft.id ? (session.payload.accounts ?? []).find((a) => a.id === draft.id) : undefined

  let passwordCt = (existing as AccountItem | undefined)?.password_ciphertext ?? null
  let fp = (existing as AccountItem | undefined)?.password_fingerprint_b64 ?? null
  let strength = (existing as AccountItem | undefined)?.password_strength ?? 'medium'
  let pwChangedAt = (existing as AccountItem | undefined)?.last_password_changed_at ?? null
  if (draft.password && draft.password.length) {
    passwordCt = sealField(o.vdk, draft.password)
    fp = passwordFingerprint(o.vdk, draft.password)
    strength = classifyStrength(estimateScore(draft.password))
    pwChangedAt = now
  }
  const noteCt = draft.note && draft.note.length ? sealField(o.vdk, draft.note) : (existing as AccountItem | undefined)?.note_ciphertext ?? null

  const item: AccountItem = {
    id: draft.id ?? uuid(),
    platform_name: draft.platform_name.trim(),
    platform_url: nz(draft.platform_url),
    avatar: (existing as AccountItem | undefined)?.avatar ?? null,
    avatar_bg: (existing as AccountItem | undefined)?.avatar_bg ?? null,
    username: nz(draft.username),
    email: nz(draft.email),
    phone: nz(draft.phone),
    password_ciphertext: passwordCt,
    password_fingerprint_b64: fp,
    password_strength: strength,
    recovery_codes_ciphertext: (existing as AccountItem | undefined)?.recovery_codes_ciphertext ?? null,
    note_ciphertext: noteCt,
    category_id: nz(draft.category_id),
    tag_ids: (existing as AccountItem | undefined)?.tag_ids ?? [],
    project_id: nz(draft.project_id),
    has_2fa: draft.has_2fa,
    status: 'active',
    last_password_changed_at: pwChangedAt,
    last_used_at: (existing as AccountItem | undefined)?.last_used_at ?? null,
    created_at: (existing as AccountItem | undefined)?.created_at ?? now,
    updated_at: now,
    favorite: draft.favorite,
  }
  const list = session.payload.accounts ?? (session.payload.accounts = [])
  const idx = list.findIndex((a) => a.id === item.id)
  if (idx >= 0) list[idx] = item as unknown as Entity
  else list.push(item as unknown as Entity)
  await sync()
}

/** 新建/更新密钥。 */
export async function upsertSecret(draft: SecretDraft): Promise<void> {
  const o = opened.value
  if (!o) return
  const now = new Date().toISOString()
  const existing = draft.id ? (session.payload.secrets ?? []).find((s) => s.id === draft.id) : undefined

  let secretCt = (existing as SecretItem | undefined)?.secret_ciphertext ?? ''
  if (draft.secret && draft.secret.length) secretCt = sealField(o.vdk, draft.secret)
  if (!secretCt) throw new Error('密钥值不能为空')
  const noteCt = draft.note && draft.note.length ? sealField(o.vdk, draft.note) : (existing as SecretItem | undefined)?.note_ciphertext ?? null

  const item: SecretItem = {
    id: draft.id ?? uuid(),
    name: draft.name.trim(),
    platform_name: nz(draft.platform_name),
    kind: draft.kind,
    key_id: nz(draft.key_id),
    secret_ciphertext: secretCt,
    project_id: nz(draft.project_id),
    environment: nz(draft.environment),
    status: (existing as SecretItem | undefined)?.status ?? 'active',
    rotation_cycle_days: draft.rotation_cycle_days ?? null,
    last_rotated_at: (existing as SecretItem | undefined)?.last_rotated_at ?? null,
    next_rotation_at: (existing as SecretItem | undefined)?.next_rotation_at ?? null,
    created_at: (existing as SecretItem | undefined)?.created_at ?? now,
    updated_at: now,
    note_ciphertext: noteCt,
  }
  const list = session.payload.secrets ?? (session.payload.secrets = [])
  const idx = list.findIndex((s) => s.id === item.id)
  if (idx >= 0) list[idx] = item as unknown as Entity
  else list.push(item as unknown as Entity)
  await sync()
}

export async function deleteEntry(kind: 'account' | 'secret', id: string): Promise<void> {
  const key = kind === 'account' ? 'accounts' : 'secrets'
  const list = (session.payload[key] ?? []) as { id: string }[]
  session.payload[key] = list.filter((e) => e.id !== id) as VaultPayload[typeof key]
  await sync()
}

function randomBytes(n: number): Uint8Array {
  const b = new Uint8Array(n)
  crypto.getRandomValues(b)
  return b
}

// ── 改 PIN / 恢复码（P6 账户安全）────────────────────────────────────────────
/**
 * 改主密码（云端 vault）：先 flush 本地未同步编辑对齐 OCC 基线 → 旧 PIN 解 VDK →
 * 新 salt 重派生 KEK 重新包装同一 VDK（rewrapVaultPin，字段密文不动）→ 一次 upload。
 * VDK 不变；成功后刷新内存 meta（key_wrap 已换），下次解锁用新 PIN。
 */
export async function changePin(oldPin: string, newPin: string): Promise<void> {
  const o = opened.value
  if (!o || !meta) throw new Error('请先解锁')
  if (newPin.length < 8) throw new Error('新主密码至少 8 位')
  if (newPin === oldPin) throw new Error('新主密码不能与旧主密码相同')
  session.error = ''
  session.busy = true
  try {
    // 先把本地未同步编辑推上去，使内存 version 与服务端一致（OCC 基线）。
    session.busyStep = '正在同步当前数据…'
    await sync()
    session.busyStep = '正在校验旧主密码并重新包装主密钥…'
    const buildCurrent = () => bytesToBase64(packVault(meta!, resealPayload(o.vdk, clone(session.payload))))
    let uploaded = rewrapVaultPin(buildCurrent(), oldPin, newPin, sServerB64)
    session.busyStep = '正在上传新包装…'
    let view: api.VaultView
    try {
      view = await api.uploadVault(version, uploaded)
    } catch (e) {
      if (!api.asVersionConflict(e)) throw e
      // 期间云端有新版本：重新同步合并后再试一次（仍是一次成功 upload）。
      await sync()
      uploaded = rewrapVaultPin(buildCurrent(), oldPin, newPin, sServerB64)
      view = await api.uploadVault(version, uploaded)
    }
    version = view.version
    meta = readMeta(uploaded) // key_wrap 已换；VDK 不变
    basePayload = clone(session.payload)
    session.syncState = 'synced'
    session.notice = '主密码已更新，下次解锁请用新主密码。'
  } catch (e) {
    session.error = friendly(e)
    throw e
  } finally {
    session.busy = false
    session.busyStep = ''
  }
}

/**
 * 重新生成恢复码：新码 → 更新 meta.settings 的 recovery_code_hash + salt → 同步回写。
 * 明文恢复码仅此一次经 session.lastRecoveryCode 展示（要求用户记录）。
 * 决策 D：web 创建/重置的恢复码不含 recovery_wrapped_vdk，SMS 重置仍需桌面端补登记一次。
 */
export async function regenerateRecoveryCode(): Promise<void> {
  if (!opened.value || !meta) throw new Error('请先解锁')
  session.error = ''
  session.busy = true
  try {
    session.busyStep = '正在生成新的恢复码…'
    const code = generateRecoveryCode()
    const salt = randomBytes(16)
    const settings = (meta.settings ?? (meta.settings = {})) as Record<string, unknown>
    settings.recovery_code_hash = recoveryCodeFingerprint(code, salt)
    settings.recovery_code_salt_b64 = bytesToBase64(salt)
    session.busyStep = '正在回写云端…'
    await sync()
    session.lastRecoveryCode = code
  } catch (e) {
    session.error = friendly(e)
    throw e
  } finally {
    session.busy = false
    session.busyStep = ''
  }
}

// ── 锁定 / 退出 ────────────────────────────────────────────────────────────
export function wipe(): void {
  if (opened.value) zeroize(opened.value.vdk)
  opened.value = null
  meta = null
  basePayload = emptyPayload()
  version = 0
  sServerB64 = ''
  session.payload = emptyPayload()
  session.lastRecoveryCode = ''
  session.conflicts = []
  session.syncState = 'idle'
}

export function lock(): void {
  wipe()
  session.stage = session.hasVault ? 'unlock' : 'setup'
}

export function logout(): void {
  wipe()
  api.clearToken()
  session.account = null
  session.membership = null
  session.hasVault = false
  session.stage = 'login'
}
